import { useLazyQuery } from '@apollo/client/react'
import cloneDeep from 'lodash-es/cloneDeep'
import isEmpty from 'lodash-es/isEmpty'
import isEqual from 'lodash-es/isEqual'
import omit from 'lodash-es/omit'
import { JSX, useCallback, useMemo, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog/gql'
import {
  EmbroideryVariantTag,
  ProductCatalogEmbroideryPlacement,
  ProductCatalogEmbroideryTypes,
} from '../../__generated__/graphql/catalog/graphql'
import { useLocalization } from '../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { isDefinedAndNotNull } from '../../app/_utils/isDefinedAndNotNull'
import { shortenShopifyIdToNumeric } from '../../app/_utils/shortenShopifyId'
import { QueryStatus } from '../../lib/hooks/types'
import { EmbroiderableProductEmbroideryConfig, ProductColor } from '../../types/graphql/product'

import {
  ExternalEmbroideryFacade,
  ExternalEmbroideryFacadeProvider,
  useExternalEmbroidery,
} from './providers/facade'
import {
  EmbroideryFormKind,
  EmbroideryFormSubmissions,
  EmbroideryFormValues,
  EmbroideryPurpose,
  EmbroiderySelections,
} from './types'
import { useEmbroideryDesignsQuery } from './useEmbroideryDesigns'
import useEmbroideryLogos from './useEmbroideryLogos'
import {
  filterEmbroideryVariantByTags,
  findDigitizationVariant,
  useEmbroideryVariantQuery,
  useEmbroideryVariantsQuery,
} from './useEmbroideryVariants'
import { useProductScopedEmbroideryPreferences } from './useProductScopedEmbroideryPreferences'
import { deriveEmbroiderySummary } from './utils/deriveEmbroiderySummary'
import {
  getConflictingEmbroideryType,
  getEmbroideryTypeForEmbroideryForm,
  getFormsForEmbroideryType,
  getSubmissionFormForEmbroideryType,
} from './utils/embroideryFormUtils'
import { generateEmbroideryLineItemProperties } from './utils/generateEmbroideryLineItemProperties'
import { getEmbroideryPriceRange } from './utils/getEmbroideryPriceRange'
import { getFlatlayImage } from './utils/getFlatlayImage'
import {
  getEmbroideryTypeSupport,
  isProductEmbroiderable as isProductEmbroiderableForConfig,
} from './utils/isProductEmbroiderable'
import { mapEmbroideryOptionToInitialFormValues } from './utils/unorganized'

import type { UseCartNewCartItem } from '../../brunswick/containers/cart'
import type { MakeCartItemProps } from '../../brunswick/containers/cart/utils'

gql(`
  fragment EmbroideryGroup on ProductCatalogVariantGroup {
    id
    embroidery {
      colorRestrictions {
        key
      }
      fontRestrictions {
        key
      }
      flatlay {
        image {
          aspectRatio
          url
        }
        offsets {
          placement {
            location
            position
          }
          rotation
          scale
          x
          y
        }
      }
      locationFlatlays {
        location
        image {
          aspectRatio
          url
        }
        offsets {
          placement {
            location
            position
          }
          rotation
          scale
          x
          y
        }
      }
      options {
        icon {
          characterLimit
          key
          lineLimit
          placements {
            location
            position
          }
        }
        text {
          characterLimit
          key
          lineLimit
          placements {
            location
            position
          }
        }
        logo {
          characterLimit
          key
          lineLimit
          placements {
            location
            position
          }
        }
      }
    }
  }
`)

const getEmbroiderySiteMessagesQuery = gql(`
  query GetEmbroiderySiteMessages {
    siteMessages(siteFixturesHandle: "default") {
      embroideryMessages {
        logoDigitizationWarning
        logoLearnMorelink
        logoColorSelectionTooltip
      }
    }
  }
`)

export interface UseEmbroideryProps {
  /** Status of the data that is being provided to this provider. */
  status: QueryStatus

  productHandle: string
  /**
   * Display title for the embroidered product. The PDP reads the title from
   * `VariantSelectionFormContext`; the cart surface has no such context, so it
   * passes the cart line's title here for the modal header to fall back to.
   */
  productTitle?: string
  /** The category of the current product group. */
  productCategoryId: string
  /** The currently selected product color identifier. */
  productColorId: string | undefined
  /** The currently selected product color hex value. */
  productColorHexCodes: ProductColor['hexCodes'] | undefined | null
  /** The config object that defines embroidery locations and restrictions for this specific product. */
  productEmbroideryConfig: EmbroiderableProductEmbroideryConfig | null | undefined

  /** Embroidery types that should be "required". */
  requiredEmbroideryTypes?: ProductCatalogEmbroideryTypes[]
  /** Enforced pre-selected embroidery choices for a given form. Cannot be edited by the user. */
  defaultEmbroiderySelections?: Partial<EmbroideryFormSubmissions> | null

  // TODO: rename this to be more clear, something like embroideryOverrideValues would be better
  /** Overridable initial form values that should be used. */
  defaultFormValues?: {
    [key in keyof EmbroideryFormValues]?: Partial<EmbroideryFormValues[key]>
  }
  /** Individual embroidery fields that should be hidden from the UI. */
  hiddenFormFields?: {
    [key in keyof EmbroideryFormValues]?: Array<keyof EmbroideryFormValues[key]>
  }
  /** Individual embroidery fields that should be disabled in the UI. */
  disabledFormFields?: {
    [key in keyof EmbroideryFormValues]?: Array<keyof EmbroideryFormValues[key]>
  }

  /** Optionally define dropdown options for the text embroidery form. Will replace the free-form text input with a dropdown if defined. */
  textEmbroideryConfig?: {
    secondLineOptions?: string[]
  }

  /** Why are we embroidering? */
  embroideryPurpose: EmbroideryPurpose

  /** True only for team-store B2B flows (legacy portal is B2B too, but not team store). */
  isTeamStoreEmbroidery?: boolean
  /**
   * Logo placements supported by the base catalog configuration (before any B2B/team-store overrides).
   * Used to gate logo preview rendering so shared flatlay offsets (e.g. icon sleeve offsets) do not
   * accidentally make unsupported logo placements previewable.
   */
  baseCatalogLogoPlacements?: ProductCatalogEmbroideryPlacement[]

  /** Optional discount message to display. */
  embroideryDiscountMessage?: string
  /** Ability to disable all price displays. */
  withPricing: boolean
  /** Whether saved account embroidery preferences should be used at all on this provider instance */
  enableEmbroideryPreferences?: boolean

  /**
   * Cart embroidery upsell config. Present only when the experience is hosted on
   * the cart surface (the PDP leaves it undefined). The provider derives
   * `surface: 'cart'` from its presence, which hides PDP-only embroidery modal
   * features such as the price summary, color selector, etc.
   */
  cartUpsell?: {
    /* The existing cart line being converted */
    item: Pick<MakeCartItemProps, 'product' | 'variant' | 'variantGroup'> & {
      quantity: number
    }
    /**
     * The modal's Add To Bag action invokes this so we can convert an item
     * already in the cart to an embroidered item.
     */
    onAddEmbroidery: (embroideredItems: UseCartNewCartItem[]) => Promise<void>
  }
}

type EmbroideryFormValueErrors = {
  [key in keyof EmbroideryFormValues]?: string
}

// Given one specific form kind and its product defaults, return the values for that same kind
type GetFormValuesForKind = <K extends EmbroideryFormKind>(
  formKind: K,
  productDefaultFormValues: EmbroideryFormValues[K]
) => EmbroideryFormValues[K]

// Assign values for one specific form kind into a keyed object
type AssignFormValuesByKind = <K extends EmbroideryFormKind>(
  formKind: K,
  formValues: EmbroideryFormValues[K]
) => void

function useEmbroideryImpl(props: UseEmbroideryProps | undefined) {
  const {
    status,
    productHandle,
    productTitle,
    productCategoryId,
    productColorId,
    productColorHexCodes,
    productEmbroideryConfig,
    requiredEmbroideryTypes = [],
    defaultEmbroiderySelections,
    defaultFormValues,
    disabledFormFields,
    hiddenFormFields,
    textEmbroideryConfig,
    withPricing,
    embroideryPurpose,
    enableEmbroideryPreferences = false,
    cartUpsell,
    isTeamStoreEmbroidery = false,
    baseCatalogLogoPlacements,
  } = props!
  const surface: 'pdp' | 'cart' = cartUpsell ? 'cart' : 'pdp'
  const cartUpsellItem = cartUpsell?.item
  const onAddEmbroidery = cartUpsell?.onAddEmbroidery

  const productDefaultFlatlayImage = productEmbroideryConfig?.flatlay
  const productLocationFlatlayImages = productEmbroideryConfig?.locationFlatlays

  const { transientLogo, userLogos } = useEmbroideryLogos()
  const translations = useTranslation()
  const { locale, region } = useLocalization()
  const { data: embroideryVariantsQueryData } = useEmbroideryVariantsQuery()
  useEmbroideryDesignsQuery() // Just priming this so its loaded ahead of time if people open the embroidery modal.
  const productColorHex: string | undefined = productColorHexCodes?.[0]

  // List of embroidery types this product supports and doesn't support.
  const [productEmbroideryOptionKeys, unsupportedEmbroideryOptionKeys] = useMemo(() => {
    const embroideryTypeSupport = getEmbroideryTypeSupport({ productEmbroideryConfig })
    const productEmbroideryOptionKeys = Object.entries(embroideryTypeSupport)
      .filter(entry => entry[1])
      .map(entry => entry[0] as ProductCatalogEmbroideryTypes)
    const unsupportedEmbroideryOptionKeys = Object.entries(embroideryTypeSupport)
      .filter(entry => !entry[1])
      .map(entry => entry[0] as ProductCatalogEmbroideryTypes)
    return [productEmbroideryOptionKeys, unsupportedEmbroideryOptionKeys]
  }, [productEmbroideryConfig])

  const resolvedBaseCatalogLogoPlacements =
    baseCatalogLogoPlacements ?? productEmbroideryConfig?.options.logo?.placements

  // If the user has "confirmed" the selections. This might not be the case if we just had defaults, or if we typed data into a form in a dialog but then hit escape to close it.
  // Though, I am skeptical this is still necessary, now that we track "checked" state for all the embroidery forms. This can probably be stripped out.
  const [confirmAddEmbroidery, setConfirmAddEmbroidery] = useState<boolean>(false)

  // Gates display of validation errors in the modal, separately from whether confirmation actually succeeded.
  const [hasAttemptedConfirm, setHasAttemptedConfirm] = useState<boolean>(false)

  // These are the default values that come from the product, for instance, TEXT may
  // start with empty strings for the lines, default thread color, and placement.
  const productDefaultFormValuesByKind = useMemo<Partial<EmbroideryFormValues>>(() => {
    if (!productEmbroideryConfig) return {}

    const formValuesByKind: Partial<EmbroideryFormValues> = {}
    const assignFormValues: AssignFormValuesByKind = (formKind, formValues) => {
      formValuesByKind[formKind] = formValues
    }
    const embroideryOptions = [
      productEmbroideryConfig.options.icon,
      productEmbroideryConfig.options.logo,
      productEmbroideryConfig.options.text,
    ]

    for (const embroideryOption of embroideryOptions) {
      if (!embroideryOption) continue

      const embroideryForms = getFormsForEmbroideryType({
        embroideryType: embroideryOption.key,
        isB2b: embroideryPurpose === 'B2B',
      })

      for (const formKind of embroideryForms) {
        assignFormValues(
          formKind,
          mapEmbroideryOptionToInitialFormValues(
            formKind,
            embroideryOption,
            productEmbroideryConfig,
            productColorHex
          )
        )
      }
    }

    return formValuesByKind
  }, [productColorHex, productEmbroideryConfig, embroideryPurpose])

  const {
    loading: isLoadingSavedEmbroideryPreferences,
    savedEmbroideryDefaultFormValues,
    savedEmbroideryPreferences,
  } = useProductScopedEmbroideryPreferences({
    defaultFormValues,
    enableEmbroideryPreferences,
    productDefaultFormValuesByKind,
    productEmbroideryConfig,
    transientLogo,
    userLogos,
  })

  const [fetchEmbroiderySiteMessages, embroiderySiteMessagesData] = useLazyQuery(
    getEmbroiderySiteMessagesQuery,
    {}
  )
  const embroiderySiteMessages = embroiderySiteMessagesData.data?.siteMessages?.embroideryMessages

  // Build a `form values by kind` map starting from the product defaults for each form kind.
  // The provided callback defines what additional values should be layered on top of those
  // product defaults, which keeps the prefilled and cleared/reset cases aligned.
  const buildFormValuesByKind = useCallback(
    (getFormValuesForKind: GetFormValuesForKind) => {
      const formValuesByKind: Partial<EmbroideryFormValues> = {}
      const assignFormValues: AssignFormValuesByKind = (formKind, formValues) => {
        formValuesByKind[formKind] = formValues
      }

      for (const formKind of Object.keys(
        productDefaultFormValuesByKind
      ) as Array<EmbroideryFormKind>) {
        const productDefaultFormValues = productDefaultFormValuesByKind[formKind]
        if (!productDefaultFormValues) continue

        assignFormValues(formKind, getFormValuesForKind(formKind, productDefaultFormValues))
      }

      return formValuesByKind
    },
    [productDefaultFormValuesByKind]
  )

  // These are the actual values a form should open with when it is first shown.
  // They start from the product defaults, then layer in any compatible saved preferences e.g.
  // if a user prefers text on the left chest and the top supports it, use those preferences,
  // and finally any defaults from `defaultFormValues`.
  const prefilledFormValuesByKind = useMemo<Partial<EmbroideryFormValues>>(() => {
    const getPrefilledFormValuesForKind: GetFormValuesForKind = (
      formKind,
      productDefaultFormValues
    ) => {
      return {
        ...productDefaultFormValues,
        ...(savedEmbroideryDefaultFormValues?.[formKind] ?? {}),
        ...(defaultFormValues?.[formKind] ?? {}),
      } as EmbroideryFormValues[typeof formKind]
    }

    return buildFormValuesByKind(getPrefilledFormValuesForKind)
  }, [buildFormValuesByKind, defaultFormValues, savedEmbroideryDefaultFormValues])

  // These are the values a form should reset back to after the user clicks Clear/Reset.
  // They intentionally keep product defaults and defaults from `defaultFormValues`,
  // but they do not restore saved account preferences.
  const clearedFormValuesByKind = useMemo<Partial<EmbroideryFormValues>>(() => {
    const getClearedFormValuesForKind: GetFormValuesForKind = (
      formKind,
      productDefaultFormValues
    ) => {
      return {
        ...productDefaultFormValues,
        ...(defaultFormValues?.[formKind] ?? {}),
      } as EmbroideryFormValues[typeof formKind]
    }

    return buildFormValuesByKind(getClearedFormValuesForKind)
  }, [buildFormValuesByKind, defaultFormValues])

  // The most recent form values the user has touched for each embroidery form kind.
  // Takes priority over saved-preference defaults so the modal reopens with the user's
  // last input rather than resetting to their account preferences.
  const [stickyFormValuesByKind, setStickyFormValuesByKind] = useState<
    Partial<EmbroideryFormValues>
  >({})

  // The submission data for each form that was derived from its form values
  const [userSubmittedEmbroiderySelections, setUserSubmittedEmbroiderySelections] = useState<
    Partial<EmbroideryFormSubmissions>
  >(defaultEmbroiderySelections ?? {})

  // Tracks which saved-preference form kinds the user has opted into from the PDP checkbox,
  // without going through the embroidery modal. Kept separate from userSubmittedEmbroiderySelections
  // so the modal never sees these as completed form submissions.
  const [pdpCheckedEmbroideryPreferenceFormKinds, setPdpCheckedEmbroideryPreferenceFormKinds] =
    useState<Set<EmbroideryFormKind>>(new Set())

  // The submitted selections derived from defaults plus submitted form data.
  // This excludes compatible saved preferences, so it represents what the user has
  // explicitly configured in the embroidery flow whether or not they have confirmed it yet.
  const submittedEmbroiderySelections = useMemo(() => {
    // build the source values on top of the default values to override potential defaults
    return cloneDeep({
      ...(defaultEmbroiderySelections ?? {}),
      ...userSubmittedEmbroiderySelections,
    })
  }, [defaultEmbroiderySelections, userSubmittedEmbroiderySelections])

  // The UI-facing selections for the rest of the PDP.
  // Before confirmation, this can show compatible saved preferences plus enforced
  // defaults so the user can preview embroidery choices in the PDP. After
  // confirmation, it switches to the submitted selections above layered on top
  // of any compatible saved preferences.
  const embroiderySelections = useMemo(() => {
    if (!confirmAddEmbroidery) {
      return cloneDeep({
        ...savedEmbroideryPreferences,
        ...(defaultEmbroiderySelections ?? {}),
      })
    } else {
      return cloneDeep({
        ...savedEmbroideryPreferences,
        ...submittedEmbroiderySelections,
      })
    }
  }, [
    savedEmbroideryPreferences,
    confirmAddEmbroidery,
    defaultEmbroiderySelections,
    submittedEmbroiderySelections,
  ])

  // TODO: These flags + the use of confirmAddEmbroidery need to be consolidated,
  // possibly into a state for embroidery confirmation status -> none, pending, confirmed.
  // Having all these separate flags, and other components doing various ways of checking
  // if embroidery has been confirmed makes it a bit complicated.
  const isProductEmbroiderable = isProductEmbroiderableForConfig({ productEmbroideryConfig })
  const hasSubmittedEmbroiderySelections = Object.keys(submittedEmbroiderySelections).length > 0

  // Whether the PDP has any embroidery to show — includes saved preferences and
  // defaults, not just what the shopper submitted in the modal.
  const hasEmbroidery = !isEmpty(embroiderySelections)
  const hasB2bLogoSelection = !!embroiderySelections.LOGO_B2B

  // Whether this embroidery type was selected (submitted) through the embroidery modal.
  // A multi-step type (e.g. DTC LOGO) is only "selected" once its submission form has been
  // submitted — uploading a logo in LOGO_SETUP records the file but must not lock the
  // LOGO type in until the LOGO_DTC "Add" step is confirmed.
  const isEmbroideryTypeSelectedInModal = useCallback(
    (embroideryType: ProductCatalogEmbroideryTypes): boolean => {
      const submissionFormKind = getSubmissionFormForEmbroideryType({
        embroideryType,
        isB2b: embroideryPurpose === 'B2B',
      })
      return Boolean(submissionFormKind && submittedEmbroiderySelections[submissionFormKind])
    },
    [embroideryPurpose, submittedEmbroiderySelections]
  )

  // Whether an embroidery type was selected from either source — the PDP saved-preference checkbox
  // or a modal submission — ignoring LOGO/ICON mutual exclusion. This is the single source of truth
  // for "selected somehow"; the conflict tiebreak is applied by isEmbroideryTypeSelectedForBag below.
  const isEmbroideryTypeSelectedFromAnySource = useCallback(
    (embroideryType: ProductCatalogEmbroideryTypes): boolean => {
      const formKinds = getFormsForEmbroideryType({
        embroideryType,
        isB2b: embroideryPurpose === 'B2B',
      })

      // Active if the user opted in via the PDP saved-preference checkbox.
      const isActiveViaPdpPreferenceCheckbox = formKinds.some(formKind =>
        pdpCheckedEmbroideryPreferenceFormKinds.has(formKind)
      )

      // Active if the user selected this type via the modal and hasn't unchecked it in the sneak peek.
      const isActiveViaModalSelection =
        isEmbroideryTypeSelectedInModal(embroideryType) &&
        !formKinds.some(
          formKind => submittedEmbroiderySelections[formKind]?.isSelectedForAddToBagAction === false
        )

      return isActiveViaPdpPreferenceCheckbox || isActiveViaModalSelection
    },
    [
      submittedEmbroiderySelections,
      embroideryPurpose,
      pdpCheckedEmbroideryPreferenceFormKinds,
      isEmbroideryTypeSelectedInModal,
    ]
  )

  // Whether an embroidery type should actually be added to the bag. Builds on
  // isEmbroideryTypeSelectedFromAnySource and resolves LOGO/ICON mutual exclusion
  const isEmbroideryTypeSelectedForBag = useCallback(
    (embroideryType: ProductCatalogEmbroideryTypes): boolean => {
      if (!isEmbroideryTypeSelectedFromAnySource(embroideryType)) return false

      const conflictingEmbroideryType = getConflictingEmbroideryType(embroideryType)

      // No conflict
      if (
        !conflictingEmbroideryType ||
        !isEmbroideryTypeSelectedFromAnySource(conflictingEmbroideryType)
      )
        return true

      // Both LOGO and ICON are selected (one via the modal, one via a PDP preference). The modal
      // submission wins: this type loses only if the conflicting type came from the modal and this
      // one didn't. That also guarantees we never drop both.
      const isCurrentSelectionViaModal = isEmbroideryTypeSelectedInModal(embroideryType)
      const isConflictViaModal = isEmbroideryTypeSelectedInModal(conflictingEmbroideryType)

      if (isConflictViaModal && !isCurrentSelectionViaModal) return false

      return true
    },
    [isEmbroideryTypeSelectedFromAnySource, isEmbroideryTypeSelectedInModal]
  )

  // The DTC logo file driving pricing and the variant query, regardless of how it was selected.
  // A logo enabled via the PDP preference checkbox only lives in savedEmbroideryPreferences
  // (surfaced through embroiderySelections), so falling back to it keeps declineColor and pricing in
  // sync. Without this, a B&W preferred logo opted in from the PDP would price as B&W but resolve to a
  // color embroidery variant.
  const dtcLogoFileForPricing =
    submittedEmbroiderySelections.LOGO_DTC?.logoFile ?? embroiderySelections.LOGO_DTC?.logoFile

  // What we would need to pay for, based on the choices we have picked for embroidery.
  const isLogoSelectedForBag = isEmbroideryTypeSelectedForBag('LOGO')
  const { data: embroideryVariant } = useEmbroideryVariantQuery({
    withIcon: isEmbroideryTypeSelectedForBag('ICON'),
    withText: isEmbroideryTypeSelectedForBag('TEXT'),
    withLogo: isLogoSelectedForBag,
    // Only decline color when the logo is the type being added — BLACK_AND_WHITE is a logo-only
    // variant tag, so applying it while adding a non-logo type (e.g. icon) would match no variant.
    declineColor:
      isLogoSelectedForBag && dtcLogoFileForPricing?.digitizationSetting === 'BLACK_WHITE',
    forB2b: embroideryPurpose === 'B2B',
  })

  // Decide if the digitization/setup SKU should be added for the selected logo and
  // include it for non-approved logos unless a setup line for that logo already exists in cart
  const embroiderySetupVariant = useMemo(() => {
    const selectedLogo = submittedEmbroiderySelections.LOGO_DTC?.logoFile
    const selectedLogoNeedsDigitization =
      Boolean(selectedLogo) && selectedLogo?.status !== 'APPROVED'
    const requiresDigitization =
      Boolean(submittedEmbroiderySelections.LOGO_SETUP) || selectedLogoNeedsDigitization
    const digitizationLogoId =
      submittedEmbroiderySelections.LOGO_SETUP?.logoFile.id ?? selectedLogo?.id ?? undefined
    const hasSetupLineForSelection = Boolean(
      digitizationLogoId && transientLogo?.id === digitizationLogoId
    )

    return requiresDigitization && !hasSetupLineForSelection
      ? findDigitizationVariant(embroideryVariantsQueryData?.embroideryVariants)
      : undefined
  }, [
    submittedEmbroiderySelections.LOGO_DTC?.logoFile,
    submittedEmbroiderySelections.LOGO_SETUP,
    embroideryVariantsQueryData?.embroideryVariants,
    transientLogo?.id,
  ])

  // The setup fee only applies when logo embroidery is going into the bag — attaching it to
  // text or icon embroidery would charge digitization for work that isn't needed.
  const embroiderySetupVariantForBag = isEmbroideryTypeSelectedForBag('LOGO')
    ? embroiderySetupVariant
    : undefined

  /* A flattened version of selections that only keeps the ones currently selected to be added to bag.
   * Merges modal submissions with saved preferences selected via the PDP checkbox. */
  const embroiderySelectionsEnabledForBag: EmbroiderySelections = useMemo(() => {
    // merge selected preferences from PDP and selections from embroidery modal.
    // Without this merge, when a user foregoes opening the embroidery modal and checks their
    // preferred embroidery, those embroidery selections would not be added to cart
    const mergedSelections: Partial<EmbroideryFormSubmissions> = {
      ...savedEmbroideryPreferences,
      ...submittedEmbroiderySelections,
    }

    const checkedOnly = Object.entries(mergedSelections)
      .filter(([formKind, _]) => {
        const embroideryType = getEmbroideryTypeForEmbroideryForm(
          formKind as keyof typeof mergedSelections
        )
        return isEmbroideryTypeSelectedForBag(embroideryType)
      })
      .map(([_, value]) => value)
      .filter(isDefinedAndNotNull)
    const flattenedSelections = checkedOnly.reduce(
      (acc: EmbroiderySelections, value) => Object.assign(acc, value),
      {}
    )
    flattenedSelections.embroideryItemVariant = embroideryVariant
    if (isEmbroideryTypeSelectedForBag('LOGO')) {
      flattenedSelections.embroiderySetupVariant = embroiderySetupVariantForBag
    }
    return flattenedSelections
  }, [
    savedEmbroideryPreferences,
    submittedEmbroiderySelections,
    embroiderySetupVariantForBag,
    embroideryVariant,
    isEmbroideryTypeSelectedForBag,
  ])

  // used by PDP sneak-peak cards when a checkbox is toggled
  const toggleEmbroiderySelectionForAddToBag = useCallback(
    (formKind: EmbroideryFormKind) => {
      const currentSelection = userSubmittedEmbroiderySelections[formKind]
      const savedPreference = savedEmbroideryPreferences?.[formKind]

      // Toggle an explicit embroidery selection that the user has already submitted through the embroidery modal
      if (currentSelection) {
        const currentlyChecked = currentSelection.isSelectedForAddToBagAction
        setUserSubmittedEmbroiderySelections({
          ...userSubmittedEmbroiderySelections,
          [formKind]: {
            ...currentSelection,
            isSelectedForAddToBagAction: currentlyChecked === undefined ? false : !currentlyChecked,
          },
        })
        // Keep the PDP in the confirmed summary state after unchecking.
        // Dropping back to the CTA/preferences state would hide the deselected option
        // instead of leaving it visible and unchecked.
        setConfirmAddEmbroidery(true)
        return
      }

      // No modal submission or saved preference for this form kind — checkbox shouldn't exist
      if (!savedPreference) return

      // Toggle the preference-selected set so the PDP checkbox reflects the choice
      // and isEmbroideryTypeSelectedForBag returns true/false accordingly, without touching
      // userSubmittedEmbroiderySelections (which would make the modal show it as selected).
      setPdpCheckedEmbroideryPreferenceFormKinds(prev => {
        const next = new Set(prev)
        if (next.has(formKind)) {
          next.delete(formKind)
        } else {
          next.add(formKind)
        }
        return next
      })
      setConfirmAddEmbroidery(true)
    },
    [savedEmbroideryPreferences, userSubmittedEmbroiderySelections]
  )

  // Line item props that will be needed for adding embroidery to cart.
  const embroideryLineItemProperties = useMemo(() => {
    if (Object.keys(embroiderySelectionsEnabledForBag).length === 0) return undefined
    const variantId: number | undefined =
      shortenShopifyIdToNumeric(
        embroiderySelectionsEnabledForBag.embroideryItemVariant?.productInfo.shopifyId
      ) ?? undefined
    const logoSetupId: number | undefined =
      shortenShopifyIdToNumeric(
        embroiderySelectionsEnabledForBag.embroiderySetupVariant?.productInfo.shopifyId
      ) ?? undefined
    return generateEmbroideryLineItemProperties(
      embroiderySelectionsEnabledForBag,
      productColorHex,
      variantId,
      logoSetupId
    )
  }, [embroiderySelectionsEnabledForBag, productColorHex])

  // The whole range of things we could end up buying for embroidery.
  const embroideryVariants = useMemo(() => {
    return (embroideryVariantsQueryData?.embroideryVariants ?? []).filter(v =>
      filterEmbroideryVariantByTags(v, {
        tagsAllOf: embroideryPurpose === 'B2B' ? ['B2B'] : ['DTC'], // Must contain all of these
        tagsOneOf: productEmbroideryOptionKeys,
        tagsNoneOf: unsupportedEmbroideryOptionKeys,
      })
    )
  }, [
    embroideryPurpose,
    embroideryVariantsQueryData?.embroideryVariants,
    productEmbroideryOptionKeys,
    unsupportedEmbroideryOptionKeys,
  ])

  // Price range of all embroidery possibilities.
  const embroideryPriceRange = useMemo(() => {
    return getEmbroideryPriceRange({
      embroideryVariants,
      relatedSetupVariant: undefined,
      locale,
    })
  }, [locale, embroideryVariants])

  // Price ranges for each embroidery type, so must exclude the combo (bundle) types.
  const embroideryPriceRangeByType = useMemo(() => {
    const embroideryPriceRangeByType: Partial<
      Record<ProductCatalogEmbroideryTypes, ReturnType<typeof getEmbroideryPriceRange>>
    > = {}
    productEmbroideryOptionKeys.forEach(embroideryType => {
      const tagsAllOf: EmbroideryVariantTag[] = [embroideryType]
      const tagsNoneOf: EmbroideryVariantTag[] = ['BUNDLE']

      if (embroideryType === 'LOGO' && dtcLogoFileForPricing) {
        if (dtcLogoFileForPricing.digitizationSetting === 'BLACK_WHITE') {
          tagsAllOf.push('BLACK_AND_WHITE')
          // TODO: Fix this auto-generated suppression.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (dtcLogoFileForPricing.digitizationSetting === 'COLOR') {
          tagsNoneOf.push('BLACK_AND_WHITE')
        }
      }

      const priceRange = getEmbroideryPriceRange({
        embroideryVariants: embroideryVariants.filter(v =>
          filterEmbroideryVariantByTags(v, {
            tagsAllOf,
            tagsNoneOf,
          })
        ),
        relatedSetupVariant: embroideryType === 'LOGO' ? embroiderySetupVariant : undefined,
        locale,
      })

      if (priceRange) embroideryPriceRangeByType[embroideryType] = priceRange
    })
    return embroideryPriceRangeByType
  }, [
    dtcLogoFileForPricing,
    embroiderySetupVariant,
    embroideryVariants,
    locale,
    productEmbroideryOptionKeys,
  ])

  const [previewItemsByKind, setPreviewItemsByKind] = useState<
    Partial<Record<EmbroideryFormKind, JSX.Element[]>>
  >({})

  const clearEmbroiderySelections = useCallback(() => {
    setConfirmAddEmbroidery(false)
    setHasAttemptedConfirm(false)
    setPreviewItemsByKind({})
    setUserSubmittedEmbroiderySelections(defaultEmbroiderySelections ?? {})
    setStickyFormValuesByKind({})
    setPdpCheckedEmbroideryPreferenceFormKinds(new Set())
  }, [defaultEmbroiderySelections])

  const clearEmbroiderySelectionsByType = useCallback(
    (embroideryType: ProductCatalogEmbroideryTypes) => {
      const formTypes = getFormsForEmbroideryType({
        embroideryType,
        isB2b: embroideryPurpose === 'B2B',
      })
      setPreviewItemsByKind(prev => omit(prev, formTypes))
      setUserSubmittedEmbroiderySelections(prev => omit(prev, formTypes))
      setStickyFormValuesByKind(prev => omit(prev, formTypes))
      // Also drop any PDP saved-preference opt-ins for this type, otherwise the type stays
      // "selected from any source" and keeps getting priced/added even after it's removed.
      setPdpCheckedEmbroideryPreferenceFormKinds(prev => {
        if (formTypes.every(formKind => !prev.has(formKind))) return prev
        const next = new Set(prev)
        formTypes.forEach(formKind => next.delete(formKind))
        return next
      })

      /**
       * If only 1 options is deleted, we have to check if we're back to default or
       * there are 0 selectedEmbroideryOptions in order to set the confirmAddEmbroidery state to false
       */
      if (
        (defaultEmbroiderySelections &&
          isEqual(userSubmittedEmbroiderySelections, defaultEmbroiderySelections)) ||
        isEmpty(userSubmittedEmbroiderySelections)
      ) {
        setConfirmAddEmbroidery(false)
      }
    },
    [embroideryPurpose, defaultEmbroiderySelections, userSubmittedEmbroiderySelections]
  )

  // The currently viewed embroidery form.
  const [selectedFormKind, setSelectedFormKind] = useState<EmbroideryFormKind | null>(null)

  // The chosen placements on the garment for each form.
  const [selectedPlacements, setSelectedPlacements] = useState<
    Partial<Record<EmbroideryFormKind, ProductCatalogEmbroideryPlacement>>
  >({})

  const setCurrentPlacement = useCallback(
    (embroideryKind: EmbroideryFormKind | null, placement: ProductCatalogEmbroideryPlacement) => {
      if (!embroideryKind) return
      setSelectedPlacements(prev => ({
        ...prev,
        [embroideryKind]: placement,
      }))
    },
    []
  )

  const currentPlacement = useMemo(() => {
    if (selectedFormKind === null) return null

    // If there is an explicitly selected placement set for the user
    const selectedPlacement = selectedPlacements[selectedFormKind]
    if (selectedPlacement) return selectedPlacement

    // Fallback to use the default placements defined by the form state
    const formValues = prefilledFormValuesByKind[selectedFormKind]
    if (formValues && 'logoPlacement' in formValues) {
      return formValues.logoPlacement ?? null
    }
    if (formValues && 'iconPlacement' in formValues) {
      return formValues.iconPlacement ?? null
    }
    if (formValues && 'textPlacement' in formValues) {
      return formValues.textPlacement ?? null
    }

    return selectedPlacements[selectedFormKind] ?? null
  }, [selectedFormKind, prefilledFormValuesByKind, selectedPlacements])

  const productFlatlayImage = useMemo(() => {
    return getFlatlayImage({
      defaultFlatlay: productDefaultFlatlayImage,
      locationFlatlays: productLocationFlatlayImages,
      location: currentPlacement?.location,
    })
  }, [currentPlacement, productDefaultFlatlayImage, productLocationFlatlayImages])

  /* Reset form values if the available embroidery options have changed */
  const embroideryOptionsKey = `${embroideryPurpose}${productEmbroideryOptionKeys.join()}`
  const embroideryOptionsKeyRef = useRef(embroideryOptionsKey)
  if (embroideryOptionsKeyRef.current !== embroideryOptionsKey) {
    embroideryOptionsKeyRef.current = embroideryOptionsKey
    clearEmbroiderySelections()
  }

  const embroiderySelectionErrors = useMemo(() => {
    const errors: EmbroideryFormValueErrors = {}

    if (
      requiredEmbroideryTypes.includes('LOGO') &&
      Boolean(productEmbroideryConfig?.options.logo) &&
      !isEmbroideryTypeSelectedForBag('LOGO')
    ) {
      if (embroideryPurpose === 'B2B') {
        errors['LOGO_B2B'] = translations.embroidery.validation.logoEmbroideryRequired
      } else {
        errors['LOGO_DTC'] = translations.embroidery.validation.logoEmbroideryRequired
      }
    }

    // Required team store logos must have a placement before add-to-bag.
    // Legacy portals do not offer placement selection, so this does not apply to them.
    if (
      isTeamStoreEmbroidery &&
      requiredEmbroideryTypes.includes('LOGO') &&
      Boolean(productEmbroideryConfig?.options.logo) &&
      isEmbroideryTypeSelectedForBag('LOGO') &&
      !submittedEmbroiderySelections.LOGO_B2B?.logoPlacement
    ) {
      errors['LOGO_B2B'] = translations.embroidery.validation.logoPlacementRequired
    }

    if (
      requiredEmbroideryTypes.includes('TEXT') &&
      Boolean(productEmbroideryConfig?.options.text) &&
      !isEmbroideryTypeSelectedForBag('TEXT')
    ) {
      errors['TEXT'] = translations.embroidery.validation.textEmbroideryRequired
    }

    return errors
  }, [
    embroideryPurpose,
    isTeamStoreEmbroidery,
    isEmbroideryTypeSelectedForBag,
    productEmbroideryConfig?.options.logo,
    productEmbroideryConfig?.options.text,
    requiredEmbroideryTypes,
    submittedEmbroiderySelections.LOGO_B2B?.logoPlacement,
    translations.embroidery.validation.logoEmbroideryRequired,
    translations.embroidery.validation.logoPlacementRequired,
    translations.embroidery.validation.textEmbroideryRequired,
  ])

  const embroiderySelectionErrorMessages = useMemo(() => {
    const errors = Object.values(embroiderySelectionErrors)
    if (
      defaultEmbroiderySelections?.TEXT &&
      !submittedEmbroiderySelections.TEXT &&
      !confirmAddEmbroidery
    ) {
      errors.push(translations.embroidery.validation.textEmbroideryConfirmationRequired)
    }
    return errors
  }, [
    confirmAddEmbroidery,
    defaultEmbroiderySelections?.TEXT,
    submittedEmbroiderySelections.TEXT,
    embroiderySelectionErrors,
    translations.embroidery.validation.textEmbroideryConfirmationRequired,
  ])

  // Everything the PDP summary card prints, resolved here so the card itself reads no state.
  const embroiderySummary = useMemo(
    () =>
      deriveEmbroiderySummary({
        confirmAddEmbroidery,
        embroideryPriceRangeByType,
        embroiderySelections,
        isEmbroideryTypeSelectedForBag,
        locale,
        productColorHex,
        region,
        requiredEmbroideryTypes,
        savedEmbroideryPreferences,
        strings: {
          customize: translations.embroidery.customize,
          editEmbroidery: translations.embroidery.editEmbroidery,
          embroideryItemLabel: translations.cart.embroideryItemLabel,
          toggleAddEmbroidery: translations.embroidery.summary.toggleAddEmbroidery,
          toggleEmbroiderySelection: translations.embroidery.toggleEmbroiderySelection,
        },
        toggleEmbroiderySelectionForAddToBag,
        transientLogo,
        withPricing,
      }),
    [
      confirmAddEmbroidery,
      embroideryPriceRangeByType,
      embroiderySelections,
      isEmbroideryTypeSelectedForBag,
      locale,
      productColorHex,
      region,
      requiredEmbroideryTypes,
      savedEmbroideryPreferences,
      translations,
      toggleEmbroiderySelectionForAddToBag,
      transientLogo,
      withPricing,
    ]
  )

  return {
    status,
    productHandle,
    productTitle,
    productFlatlayImage,
    productColorId,
    productCategoryId,
    productColorHex,
    isProductEmbroiderable,
    isTeamStoreEmbroidery,
    baseCatalogLogoPlacements: resolvedBaseCatalogLogoPlacements,
    productEmbroideryConfig,
    productEmbroideryOptionKeys,
    embroiderySelections,
    hasEmbroidery,
    hasB2bLogoSelection,
    submittedEmbroiderySelections,
    hasSubmittedEmbroiderySelections,
    savedEmbroideryDefaultFormValues,
    savedEmbroideryPreferences,
    isLoadingSavedEmbroideryPreferences,
    defaultEmbroiderySelections,
    embroideryPriceRangeByType,
    embroiderySummary,
    embroiderySelectionsEnabledForBag,
    embroideryItemVariant: embroideryVariant,
    embroiderySetupVariant: embroiderySetupVariantForBag,
    requiredEmbroideryTypes,
    setEmbroiderySelections: setUserSubmittedEmbroiderySelections,
    embroiderySelectionErrors,
    embroiderySelectionErrorMessages,
    textEmbroideryConfig,
    currentPlacement,
    setCurrentPlacement,
    clearEmbroiderySelections,
    clearEmbroiderySelectionsByType,
    confirmAddEmbroidery,
    setConfirmAddEmbroidery,
    hasAttemptedConfirm,
    setHasAttemptedConfirm,
    embroideryLineItemProperties,
    disabledFormFields,
    hiddenFormFields,
    embroideryPriceRange,
    selectedFormKind,
    setSelectedFormKind,
    previewItemsByKind,
    setPreviewItemsByKind,
    setStickyFormValuesByKind,
    stickyFormValuesByKind,
    prefilledFormValuesByKind,
    clearedFormValuesByKind,
    withPricing,
    surface,
    onAddEmbroidery,
    cartUpsellItem,
    isEmbroideryTypeSelectedInModal,
    isEmbroideryTypeSelectedForBag,
    toggleEmbroiderySelectionForAddToBag,
    isB2bEmbroidery: embroideryPurpose === 'B2B',
    setPdpCheckedEmbroideryPreferenceFormKinds,
    embroiderySiteMessages,
    fetchEmbroiderySiteMessages,
  }
}

const EmbroideryContainer = createContainer(useEmbroideryImpl)

/**
 * Publishes this provider's value under the external embroidery contract, so code outside the
 * modal reads through the contract instead of this provider directly. `satisfies` fails to
 * compile if this provider stops filling a field the contract promises.
 */
const ExternalEmbroideryFacadeFill = ({ children }: { children?: React.ReactNode }) => {
  const embroidery = EmbroideryContainer.useContainer()
  const { open } = EmbroideryDialogContainer.useContainer()

  const facade = useMemo(
    () => ({ ...embroidery, openEmbroideryModal: open }) satisfies ExternalEmbroideryFacade,
    [embroidery, open]
  )

  return (
    <ExternalEmbroideryFacadeProvider value={facade}>{children}</ExternalEmbroideryFacadeProvider>
  )
}

/**
 * Reads embroidery for a part outside the modal.
 *
 * Typed as this provider's full value so existing callers keep compiling, but a page served by
 * the new providers only gets the `ExternalEmbroideryFacade` fields at runtime. The parity suite
 * is what checks that those fields are enough, since the compiler no longer can.
 */
export const useEmbroidery = (): ReturnType<typeof useEmbroideryImpl> & ExternalEmbroideryFacade =>
  useExternalEmbroidery() as ReturnType<typeof useEmbroideryImpl> & ExternalEmbroideryFacade

function useEmbroideryDialogImpl() {
  const {
    setSelectedFormKind,
    setConfirmAddEmbroidery,
    setHasAttemptedConfirm,
    embroiderySelectionErrors,
    setPdpCheckedEmbroideryPreferenceFormKinds,
    setEmbroiderySelections,
    fetchEmbroiderySiteMessages,
  } = EmbroideryContainer.useContainer()
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const open = useCallback(async () => {
    setHasAttemptedConfirm(false)
    setIsOpen(true)
    await fetchEmbroiderySiteMessages()
  }, [setHasAttemptedConfirm, fetchEmbroiderySiteMessages])

  const confirmAndClose = useCallback(() => {
    setHasAttemptedConfirm(true)
    if (isEmpty(embroiderySelectionErrors)) {
      setPdpCheckedEmbroideryPreferenceFormKinds(new Set())
      // Re-enable any selections the user unchecked on the PDP
      setEmbroiderySelections(
        prev =>
          Object.fromEntries(
            Object.entries(prev).map(([key, value]) => [
              key,
              value.isSelectedForAddToBagAction === false
                ? { ...value, isSelectedForAddToBagAction: undefined }
                : value,
            ])
          ) as typeof prev
      )
      setConfirmAddEmbroidery(true)
      setSelectedFormKind(null)
      setIsOpen(false)
    }
  }, [
    setConfirmAddEmbroidery,
    setHasAttemptedConfirm,
    embroiderySelectionErrors,
    setSelectedFormKind,
    setPdpCheckedEmbroideryPreferenceFormKinds,
    setEmbroiderySelections,
  ])

  const close = useCallback(() => {
    setSelectedFormKind(null)
    setIsOpen(false)
  }, [setSelectedFormKind, setIsOpen])

  return {
    close,
    confirmAndClose,
    open,
    isOpen,
  }
}

const EmbroideryDialogContainer = createContainer(useEmbroideryDialogImpl)

export const EmbroideryProvider = ({
  initialState,
  children,
}: {
  initialState: UseEmbroideryProps
  children?: React.ReactNode
}) => {
  return (
    <EmbroideryContainer.Provider initialState={initialState}>
      <EmbroideryDialogContainer.Provider>
        <ExternalEmbroideryFacadeFill>{children}</ExternalEmbroideryFacadeFill>
      </EmbroideryDialogContainer.Provider>
    </EmbroideryContainer.Provider>
  )
}

export const useEmbroideryDialog = EmbroideryDialogContainer.useContainer
